import { useCallback, useEffect, useRef, useState } from 'react';

import { FilterMatchMode } from 'primereact/api';
import type {
  DataTableFilterMetaData,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import type { selectedDate } from 'types/interfaces';

import { getDayDateFilter, getTomorrowDateFilter } from '../distributionMode';

const initialFilters: Record<string, DataTableFilterMetaData> = {
  id: { value: null, matchMode: FilterMatchMode.CONTAINS },
  name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  taskType: { value: null, matchMode: FilterMatchMode.CONTAINS },
  departmentName: { value: null, matchMode: FilterMatchMode.CONTAINS },
  creationDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
  taskStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
  plannedStartDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
  plannedEndDate: { value: null, matchMode: FilterMatchMode.CONTAINS }
};

export function useDistributionTableState({
  visible,
  defaultDateFilter,
  travelTimeOptimization
}: {
  visible: boolean;
  defaultDateFilter: selectedDate;
  travelTimeOptimization: boolean;
}) {
  const [dateFilter, setDateFilter] = useState<selectedDate>({
    dateFrom: '',
    dateUntil: ''
  });
  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(null);
  const [filters, setFilters] =
    useState<Record<string, DataTableFilterMetaData>>(initialFilters);
  const isFirstDateRender = useRef(true);
  const wasVisible = useRef(false);

  useEffect(() => {
    if (visible && !wasVisible.current) {
      setDateFilter(
        travelTimeOptimization
          ? getTomorrowDateFilter()
          : { ...defaultDateFilter }
      );
      setFilters(
        Object.fromEntries(
          Object.entries(initialFilters).map(([k, v]) => [
            k,
            { ...v, value: null }
          ])
        ) as Record<string, DataTableFilterMetaData>
      );
      setMultiSortMeta(null);
      isFirstDateRender.current = true;
    }
    wasVisible.current = visible;
  }, [visible, defaultDateFilter, travelTimeOptimization]);

  const onSortChange = useCallback((e: DataTableStateEvent) => {
    setMultiSortMeta(e.multiSortMeta ?? null);
  }, []);

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    setFilters(e.filters as Record<string, DataTableFilterMetaData>);
  }, []);

  const onDateFilterChange = useCallback((selection: selectedDate) => {
    if (isFirstDateRender.current) {
      isFirstDateRender.current = false;
      return;
    }
    const fromValid =
      selection.dateFrom !== null &&
      selection.dateFrom !== '' &&
      !isNaN(new Date(selection.dateFrom)?.getTime());
    const untilValid =
      selection.dateUntil !== null &&
      selection.dateUntil !== '' &&
      !isNaN(new Date(selection.dateUntil)?.getTime());
    setDateFilter({
      dateFrom: fromValid
        ? String(new Date(selection.dateFrom!).getTime())
        : '',
      dateUntil: untilValid
        ? String(new Date(selection.dateUntil!).getTime())
        : ''
    });
  }, []);

  const onSingleDayChange = useCallback((date: Date) => {
    setDateFilter(getDayDateFilter(date));
  }, []);

  return {
    dateFilter,
    setDateFilter,
    multiSortMeta,
    filters,
    onSortChange,
    onFiltersChange,
    onDateFilterChange,
    onSingleDayChange
  };
}
